import { Link } from "react-router-dom";
import { getVehiclePath } from "../Utils/vehicleUrls";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const number = new Intl.NumberFormat("en-US");

export default function InventoryIntelligenceReport({ data, loading, error, sort, onSortChange, showRooftop = false }) {
  const summary = data?.summary ?? {};
  const cards = [
    ["Active Inventory", summary.activeInventory],
    ["Vehicles With Mestimate", summary.withMestimate],
    ["Above Mestimate", summary.aboveMestimate],
    ["Near Estimate", summary.nearMestimate],
    ["Below Mestimate", summary.belowMestimate],
    ["No Mestimate", summary.noMestimate],
  ];
  return (
    <>
      <p className="mb-5 text-sm text-gray-500">{freshnessText(data)}</p>
      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <section aria-label="Inventory summary" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6 mb-5">
        {cards.map(([label, value]) => <div key={label} className="rounded-xl border bg-white p-4"><div className="text-xs text-gray-500">{label}</div><div className="mt-1 text-2xl font-semibold text-gray-900">{loading ? "—" : number.format(value ?? 0)}</div></div>)}
      </section>
      <div className="mb-3 flex justify-end"><label className="text-sm text-gray-600">Sort{" "}
        <select aria-label="Sort inventory intelligence" value={sort} onChange={(event) => onSortChange(event.target.value)} className="ml-2 rounded-lg border border-gray-300 bg-white px-3 py-2">
          <option value="difference_desc">Difference: highest first</option><option value="difference_asc">Difference: lowest first</option>
          <option value="price_desc">Price: high to low</option><option value="price_asc">Price: low to high</option>
          <option value="mestimate_desc">Mestimate: high to low</option><option value="mestimate_asc">Mestimate: low to high</option>
          <option value="mileage_desc">Mileage: high to low</option><option value="mileage_asc">Mileage: low to high</option>
          <option value="inventory_age_desc">Days in inventory: high to low</option><option value="inventory_age_asc">Days in inventory: low to high</option>
        </select>
      </label></div>
      <div className="overflow-x-auto rounded-xl border bg-white"><table className="w-full min-w-[1180px] text-sm">
        <thead className="bg-gray-100 text-gray-700"><tr>
          <th className="p-3 text-left">Vehicle</th>{showRooftop && <th className="p-3 text-left">Rooftop</th>}<th className="p-3 text-left">VIN</th><th className="p-3 text-right">Price</th>
          <th className="p-3 text-right">Mestimate</th><th className="p-3 text-right">Difference</th><th className="p-3 text-left">Position</th>
          <th className="p-3 text-right">Mileage</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Mestimate updated</th>
          <th className="p-3 text-left">Confirmed</th><th className="p-3 text-right">Days in inventory</th>
        </tr></thead>
        <tbody>{loading ? <tr><td colSpan={11} className="p-8 text-center text-gray-500">Loading inventory intelligence…</td></tr>
          : !error && (data?.rows?.length ?? 0) === 0 ? <tr><td colSpan={11} className="p-8 text-center text-gray-500">No active inventory found.</td></tr>
          : (data?.rows ?? []).map((row) => <tr key={row.carId} className="border-t hover:bg-gray-50">
            <td className="p-3 font-medium"><Link className="text-blue-700 hover:underline" to={getVehiclePath({ id: row.carId, year: row.year, make: row.make, model: row.model, trim: row.trim })} target="_blank" rel="noopener noreferrer">{row.year} {row.make} {row.model} {row.trim}</Link></td>
            {showRooftop && <td className="p-3">{row.rooftopDealerName || `Dealer ${row.rooftopDealerId}`}</td>}
            <td className="p-3">{row.vin || "—"}</td><td className="p-3 text-right">{money.format(row.price)}</td><td className="p-3 text-right">{row.mestimate == null ? "—" : money.format(row.mestimate)}</td>
            <td className="p-3 text-right">{formatDifference(row)}</td><td className="p-3">{formatPosition(row.position)}</td><td className="p-3 text-right">{number.format(row.mileage)}</td>
            <td className="p-3">{row.mestimateStatus || "—"}</td><td className="p-3">{formatDateTime(row.mestimateUpdatedAt)}</td><td className="p-3">{formatDateTime(row.lastConfirmedDate)}</td>
            <td className="p-3 text-right">{daysSince(row.insertDate)}</td>
          </tr>)}</tbody>
      </table></div>
    </>
  );
}

function freshnessText(data) {
  if (!data?.isAllRooftops) return `Inventory last refreshed: ${formatDateTime(data?.inventoryLastRefreshedAt)}`;
  if (data.inventoryFreshnessIncomplete) {
    return data.inventoryLastRefreshedAt
      ? `All rooftops refresh incomplete; oldest known refresh: ${formatDateTime(data.inventoryLastRefreshedAt)}`
      : "All rooftops refresh status is unknown.";
  }
  return `All rooftops refreshed by: ${formatDateTime(data.inventoryLastRefreshedAt)}`;
}

export function formatDifference(row) {
  if (row?.differenceAmount == null) return "—";
  const amount = Number(row.differenceAmount); const percent = Number(row.differencePercent) * 100;
  const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
  return `${sign}${money.format(Math.abs(amount))} (${sign}${Math.abs(percent).toFixed(1)}%)`;
}
export function formatPosition(position) {
  if (position === "Near") return "Near estimate";
  if (position === "Above") return "Above Mestimate";
  if (position === "Below") return "Below Mestimate";
  return "—";
}
function formatDateTime(value) { return value ? new Date(value).toLocaleString() : "—"; }
function daysSince(value) { return value ? `${Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86400000))}d` : "—"; }
